/**
 * Lock-Aware Segmentation
 * Phase 3: Find spot chains for aircraft with temporal lock constraints
 *
 * This module extends the normal segmentation logic to handle aircraft with
 * time-bounded spot locks (hard constraints). Locked segments MUST use the
 * specified spot, while non-locked segments use greedy optimization.
 */

import { dwellMask } from "./bitmap";
import { splitDwellByLocks, validateLock } from "./lockValidation";
import { getAvailableSpots } from "./spotFiltering";
import { ParkingRequest, PlacementSegment, Spot, TemporalLock } from "./types";
import { calculateSpotWaste } from "./wasteScoring";

/**
 * Spot chain result
 */
export type SpotChain = {
  segments: PlacementSegment[];
  moveCount: number;
};

/**
 * Lock placement failure details
 */
export type LockFailureDetails = {
  failureType: "lock_validation" | "no_spots" | "move_cap";
  failedLock?: TemporalLock;
  validationReason?: string;
  requiredMoves?: number;
  moveCap?: number;
};

/**
 * Result from findSpotChainWithLocks - success or detailed failure
 */
export type SpotChainResult =
  | { success: true; chain: SpotChain }
  | { success: false; failure: LockFailureDetails };

/**
 * Find spot chain for aircraft with lock constraints
 *
 * This function handles placement of aircraft with time-bounded spot locks.
 * Locks act as hard constraints - the algorithm MUST place aircraft in the
 * locked spot during the lock period (or fail).
 *
 * Algorithm:
 * 1. Split dwell into segments based on lock boundaries
 * 2. For locked segments: Validate and use locked spot
 * 3. For non-locked segments: Use greedy optimization (same as normal placement)
 * 4. Check move count against cap
 *
 * @param request - Parking request with optional locks
 * @param spots - Available spots
 * @param plannedMoveCap - Maximum moves allowed
 * @param bufferMinutes - Buffer around dwell
 * @param horizonHours - Planning horizon
 * @param egressWeight - Egress penalty weight (default 1.0)
 * @param wasteWeight - Waste penalty weight (default 1.0)
 * @returns Spot chain result with success or detailed failure
 */
export function findSpotChainWithLocks(
  request: ParkingRequest,
  spots: Spot[],
  plannedMoveCap: number,
  bufferMinutes: number,
  horizonHours: number,
  _egressWeight: number = 1.0,
  wasteWeight: number = 1.0
): SpotChainResult {
  console.log(
    `[findSpotChainWithLocks] Request ${request.tail_number}, locks: ${
      request.locks?.length || 0
    }`
  );

  // Split dwell by lock boundaries
  const timeSegments = splitDwellByLocks(request, bufferMinutes);
  console.log(
    `[findSpotChainWithLocks] Time segments: ${timeSegments.length}`,
    JSON.stringify(
      timeSegments.map((s) => ({
        start: s.start,
        end: s.end,
        locked: s.lockedSpotId || "none",
      }))
    )
  );

  const chain: PlacementSegment[] = [];

  // Process each time segment
  for (const segment of timeSegments) {
    if (segment.lockedSpotId) {
      // ===== LOCKED SEGMENT =====
      // Must use specified spot (hard constraint)

      const spot = spots.find((s) => s.id === segment.lockedSpotId);
      const lock = request.locks!.find(
        (l) => l.spotId === segment.lockedSpotId
      );

      if (!spot || !lock) {
        console.warn(
          `[findSpotChainWithLocks] Lock validation failed: spot ${segment.lockedSpotId} not found`
        );
        return {
          success: false,
          failure: {
            failureType: "lock_validation",
            failedLock: lock,
            validationReason: `Spot ${segment.lockedSpotId} not found`,
          },
        };
      }

      // Validate lock (size + availability)
      const validation = validateLock(lock, request, spots, horizonHours);
      if (!validation.valid) {
        console.warn(
          `[findSpotChainWithLocks] Lock validation failed: ${validation.reason}`
        );
        return {
          success: false,
          failure: {
            failureType: "lock_validation",
            failedLock: lock,
            validationReason: validation.reason,
          },
        };
      }

      // Add locked segment
      chain.push({
        spotId: spot.id,
        orientationId: spot.orientations[0].id,
        startBucket: segment.start,
        endBucket: segment.end,
      });

      console.log(
        `[findSpotChainWithLocks] Locked segment: ${spot.id} [${segment.start}, ${segment.end})`
      );
    } else {
      // ===== NON-LOCKED SEGMENT =====
      // Use greedy optimization (same logic as normal placement)

      // Create temporary request for this segment
      const segmentRequest: ParkingRequest = {
        ...request,
        arrivalBucket: segment.start,
        departureBucket: segment.end,
      };

      // Create mask for segment (no buffer - already applied in split)
      const segmentMask = dwellMask(segmentRequest, 0, horizonHours);

      // Get available spots for this segment
      const availableSpots = getAvailableSpots(
        spots,
        segmentRequest,
        segmentMask,
        {}, // Empty placement (segments are independent)
        undefined,
        undefined
      );

      if (availableSpots.length === 0) {
        console.warn(
          `[findSpotChainWithLocks] No available spots for non-locked segment [${segment.start}, ${segment.end})`
        );
        return {
          success: false,
          failure: {
            failureType: "no_spots",
            validationReason: `No available spots for non-locked segment [${segment.start}, ${segment.end})`,
          },
        };
      }

      // Score spots (waste only - egress scoring would require final placement context)
      // In a real implementation, you might want to defer egress scoring until all segments are known
      const scoredSpots = availableSpots.map((spot) => {
        const wasteScore = calculateSpotWaste(segmentRequest, spot);
        const score = wasteScore * wasteWeight;
        return { spot, score };
      });

      // Pick best spot (lowest score)
      scoredSpots.sort((a, b) => a.score - b.score);
      const bestSpot = scoredSpots[0].spot;

      // Add non-locked segment
      chain.push({
        spotId: bestSpot.id,
        orientationId: bestSpot.orientations[0].id,
        startBucket: segment.start,
        endBucket: segment.end,
      });

      console.log(
        `[findSpotChainWithLocks] Non-locked segment: ${bestSpot.id} [${segment.start}, ${segment.end}) (waste=${scoredSpots[0].score})`
      );
    }
  }

  // Count ACTUAL moves = spot changes, not segment boundaries
  // Example: [A1, A1, A1] = 0 moves, [A1, B2, A1] = 2 moves
  let actualMoves = 0;
  for (let i = 1; i < chain.length; i++) {
    if (chain[i].spotId !== chain[i - 1].spotId) {
      actualMoves++;
    }
  }

  if (actualMoves > plannedMoveCap) {
    console.warn(
      `[findSpotChainWithLocks] Move count ${actualMoves} exceeds cap ${plannedMoveCap}`
    );
    return {
      success: false,
      failure: {
        failureType: "move_cap",
        requiredMoves: actualMoves,
        moveCap: plannedMoveCap,
        validationReason: `Requires ${actualMoves} moves, exceeds cap of ${plannedMoveCap}`,
      },
    };
  }

  console.log(
    `[findSpotChainWithLocks] Success: ${chain.length} segments, ${actualMoves} moves`
  );
  return {
    success: true,
    chain: { segments: chain, moveCount: actualMoves },
  };
}
